import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import * as XLSX from 'xlsx';
import { useBoundStore } from 'useStore';
import useExportProductMasterData from '../../Queries/ExportProductMasterData';
import useCurrentMasterCountry from 'views/Components/Shared/hooks/useCurrentMasterCountry';

export default function DragProductMasterFile({
	channelKeys,
	handleClose,
	setLoading,
	loading,
	handleSentData,
}) {
	const localChannels = useBoundStore((state) => state.localChannels);
	const setLocalChannels = useBoundStore((state) => state.setLocalChannels);
	const setAlertState = useBoundStore((state) => state.setAlertState);

	const mainCountry = useCurrentMasterCountry();

	const mainCountryId = mainCountry?.id;
	const { data } = useExportProductMasterData();
	const rows = data.data;

	const onDrop = useCallback(
		(acceptedFiles) => {
			setLoading(true);
			for (const file of acceptedFiles) {
				const reader = new FileReader();

				reader.onload = async (e) => {
					try {
						const arrayBuffer = e.target.result;
						const workbook = XLSX.read(arrayBuffer, { type: 'array' });
						const sheetName = workbook.SheetNames[0];
						const sheet = workbook.Sheets[sheetName];
						const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

						// primera fila = opciones válidas → la ignoramos
						if (
							data.length > 0 &&
							data[0][0]?.toString().includes('Opciones válidas')
						) {
							data.shift();
						}

						// primera fila = encabezados
						const headers = data[0];

						if (!headers) {
							throw new Error('El archivo no tiene encabezados.');
						}

						// Validar que todos los channelKeys existan en los headers
						const missingChannels = channelKeys.filter(
							(ch) => !headers.includes(ch)
						);

						// 🔹 Validación de canales extra
						const extraChannels = headers.filter(
							(h) =>
								h !== 'SKU' &&
								h !== 'Category Manager' &&
								!channelKeys.includes(h)
						);

						if (missingChannels.length > 0) {
							setAlertState({
								open: true,
								title: 'Faltan encabezados en el archivo',
								message: `El archivo no incluye la columna ${missingChannels.join(
									', '
								)}. Para continuar, agrega esa columna y vuelve a importarlo.`,
								severity: 'error',
							});

							setLoading(false);
							return;
						}

						if (extraChannels.length > 0) {
							setAlertState({
								open: true,
								title: 'El archivo tiene columnas extra.',
								message: `El archivo incluye la columna ${extraChannels.join(
									', '
								)} y no es reconocida. Para continuar, elimina esa columna y vuelve a importarlo.`,
								severity: 'error',
							});
							setLoading(false);
							return;
						}

						// Obtener índices
						const skuIndex = headers.findIndex((h) => h === 'SKU');
						const channelIndices = channelKeys.map((ch) =>
							headers.findIndex((h) => h === ch)
						);

						const updatedLocalChannels = { ...localChannels };
						const changeLog = {};

						// Recorrer filas
						for (let i = 1; i < data.length; i++) {
							const row = data[i];
							const sku = row[skuIndex];
							if (!sku) continue;

							// Canales desde el archivo
							const channelsForSku = [];
							channelIndices.forEach((colIndex, idx) => {
								const cellValue = row[colIndex]
									?.toString()
									.trim()
									.toLowerCase();

								if (
									cellValue === '✔' ||
									cellValue === 'si' ||
									cellValue === 'v'
								) {
									channelsForSku.push(channelKeys[idx]); // verdadero
								}
								//  falsos
								else if (['x', 'no', 'f'].includes(cellValue)) {
								}
							});

							// Canales actuales desde rows
							const currentRow = rows.find((r) => r.sku === sku);
							const currentChannels = [];
							if (currentRow) {
								channelKeys.forEach((ch) => {
									if (currentRow[ch] === true) currentChannels.push(ch);
								});
							}

							// Comparar arrays (sin importar orden)
							const sortedCurrent = [...currentChannels].sort();
							const sortedNew = [...channelsForSku].sort();

							const isSame =
								sortedCurrent.length === sortedNew.length &&
								sortedCurrent.every((c, idx) => c === sortedNew[idx]);

							// Solo guardar cambios
							if (!isSame) {
								updatedLocalChannels[sku] = channelsForSku;
								// Opcional: guardar log de cambios
								changeLog[sku] = channelKeys
									.filter(
										(ch) =>
											currentChannels.includes(ch) !==
											channelsForSku.includes(ch)
									)
									.map(
										(ch) =>
											`${ch}: ${currentChannels.includes(ch) ? '✔' : 'X'} → ${
												channelsForSku.includes(ch) ? '✔' : 'X'
											}`
									);
							}
						}

						if (Object.keys(updatedLocalChannels).length === 0) {
							setAlertState({
								open: true,
								title: 'Archivo sin cambios detectados.',
								message:
									'El archivo se procesó correctamente, pero no se encontraron modificaciones para importar.',
								severity: 'info',
							});
							setLoading(false);
							return;
						}

						console.log('Change Log:', changeLog);

						// setLocalChannels(updatedLocalChannels);

						// transformamos los datos y los enviamos
						const transformData = () => {
							return Object.entries(updatedLocalChannels).map(
								([sku, selectedChannelKeys]) => {
									const marketplaceKeysSelected = selectedChannelKeys;

									const status = channelKeys.reduce((acc, key) => {
										acc[key] = marketplaceKeysSelected.includes(key);
										return acc;
									}, {});

									return {
										sku,
										status,
										country: mainCountryId,
									};
								}
							);
						};

						const TransformedData = transformData();
						handleSentData(TransformedData);

						setAlertState({
							open: true,
							message: 'Archivo importado correctamente',
							severity: 'success',
						});
						handleClose();
						setLoading(false);
					} catch (error) {
						console.error('Error al importar archivo:', error);
						setAlertState({
							open: true,
							message: 'Error al importar archivo',
							severity: 'error',
						});
						setLoading(false);
					}
				};

				reader.readAsArrayBuffer(file);
				// setLocalChannels({});
			}
		},
		[
			channelKeys,
			localChannels,
			setLocalChannels,
			setAlertState,
			handleClose,
			setLoading,
		]
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: '.xlsx, .xls',
		maxSize: 2 * 1024 * 1024, // 2 MB
		onDropRejected: () => {
			setAlertState({
				open: true,
				message: 'Archivo rechazado. Solo se permiten archivos Excel',
				severity: 'error',
			});
		},
	});

	return (
		<div
			{...getRootProps()}
			style={{
				border: '2px dashed #ccc',
				padding: '20px',
				margin: '20px',
				height: '75%',
				textAlign: 'center',
				display: 'flex',
				justifyContent: 'center',
			}}
		>
			<input {...getInputProps()} disabled={loading} />
			{isDragActive ? (
				<>
					<div
						style={{
							display: 'flex',
							alignContent: 'center',
							flexWrap: 'nowrap',
							justifyContent: 'center',
							alignItems: 'center',
							flexDirection: 'column',
						}}
					>
						<CloudDownloadIcon />
						<p>Suelta el archivo aquí...</p>
					</div>
				</>
			) : (
				<>
					<div
						style={{
							display: 'flex',
							alignContent: 'center',
							flexWrap: 'nowrap',
							justifyContent: 'center',
							alignItems: 'center',
							flexDirection: 'column',
						}}
					>
						<CloudUploadIcon />
						<p>
							Arrastra un archivo aquí, o haz clic para seleccionar un archivo
						</p>
						<Button variant="contained" disabled={loading}>
							Subir archivo
						</Button>
					</div>
				</>
			)}
		</div>
	);
}
